<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useAccountProfiles, useLocalStorage, LocalStorageItem, useUserPreferences } from '@apparatix/composables';
import { button } from '@apparatix/theme/Ai';
import { Jumbotron, Stack, Typography, Toolbar } from '@apparatix/library/core';
import AccountProfileSection from './AccountProfileSection.vue';
import AccountProfilesLoader from './AccountProfilesLoader.vue';
import { SectionEditCompleteData } from '@apparatix/types/AccountProfiles';
import AiSparklesIcon from '@apparatix/icons/AiSparklesIcon.vue';
import AiDisclaimer from './AiDisclaimer.vue';
import FeatureGate from '@apparatix/library/FeatureGate.vue';
import { useEcho } from '@laravel/echo-vue';
import InfoCircleIcon from '@primeicons/vue/info-circle';
import SparklesIcon from '@primeicons/vue/sparkles';
import { AppStore } from '../stores/AppStore';

type Props = {
  accountId: number;
}

type AccountProfilePromptPreferenceKey = `profiles:accounts:show-ai-prompt:${number}`;

interface ProfileAiPromptItem extends LocalStorageItem<boolean> {
  key: AccountProfilePromptPreferenceKey;
}
const CONFIRM_DIALOG_GROUP = 'generate-account-profile';

const props = defineProps<Props>();

const { getItem, setItem } = useLocalStorage();
const {
  allowedSectionAttempts,
  generateProfiles,
  loadProfile,
  sections,
  hasGeneratedProfile,
  generatingProfile,
  isDisclaimerAccepted,
  isGeneratingOnPageOpen,
  isLoading,
  isSectionLoadingOnPageOpen,
  manuallyUpdateSection,
  setSectionLoading,
} = useAccountProfiles();
const {
  updatePreference,
} = useUserPreferences();
const confirm = useConfirm();

const showAiPromptPreference = ref(true);

const shouldHideAiPrompt = computed(() => hasGeneratedProfile.value || !showAiPromptPreference.value);

const confirmDisclaimerAccepted = async () => {
  if (isDisclaimerAccepted.value) {
    await onGenerateProfileClick();
    return;
  }
  confirm.require({
    group: 'ProfileDisclaimer',
    header: 'Disclaimer',
    rejectProps: {
      label: 'Decline',
      size: 'small',
      severity: 'secondary',
    },
    acceptProps: {
      label: 'Accept',
      size: 'small',
    },
    accept: async () => {
      updatePreference({ aiDisclaimerAccepted: 'yes' });
      isDisclaimerAccepted.value = true;
      await onGenerateProfileClick();
    },
  });
};

const onGenerateProfileClick = async () => {
  if (hasGeneratedProfile.value) {
    confirm.require({
      group: CONFIRM_DIALOG_GROUP,
      header: 'Generate Profile',
      message: 'This will overwrite all Profile content.',
      icon: InfoCircleIcon,
      rejectProps: {
        label: 'Cancel',
        size: 'small',
        severity: 'secondary',
      },
      acceptProps: {
        label: 'Confirm',
        size: 'small',
      },
      accept: async () => {
        await generateProfiles(props.accountId);
      },
    });
  } else {
    await generateProfiles(props.accountId);
  }
};

const onCloseAiPrompt = () => {
  showAiPromptPreference.value = false;
  const preference: ProfileAiPromptItem = {
    key: `profiles:accounts:show-ai-prompt:${props.accountId}`,
    value: false,
  };
  setItem(preference);
};

const isSectionLoading = ref(false);

const onSectionEdit = async (e: SectionEditCompleteData) => {
  e.accountId = props.accountId;
  setSectionLoading(true, e.id);
  await manuallyUpdateSection(e);
};

const onSectionLoading = (data: { isLoading: boolean, id: number }) => {
  isSectionLoading.value = data.isLoading;
  setSectionLoading(data.isLoading, data.id);
};

onMounted(() => {
  loadProfile(props.accountId);
  const wantsToHideAiPrompt = getItem<boolean>(`profiles:accounts:show-ai-prompt:${props.accountId}`);

  if (typeof wantsToHideAiPrompt !== 'boolean') {
    showAiPromptPreference.value = true;
  } else {
    showAiPromptPreference.value = wantsToHideAiPrompt;
  }
});


useEcho(`tenants.${AppStore.tenantId}.entity.${props.accountId}.profile`, 'ProfileGenerated',
  () => {
    // If we found something was loading when the page opened, then lets update when we get it. Otherwise, we can assume they left the page open waiting for response on api call.
    if (isGeneratingOnPageOpen.value || isSectionLoadingOnPageOpen.value) {
      loadProfile(props.accountId);
    }
  },
);

</script>
<template>
  <AccountProfilesLoader v-if="isLoading && generatingProfile" operation="generate" />
  <AccountProfilesLoader v-if="isLoading && !generatingProfile" operation="load" />
  <Stack v-if="!isLoading">
    <FeatureGate has="feature-ai-profiles">
      <Jumbotron v-if="!shouldHideAiPrompt" class="apx-ai-border" @close="onCloseAiPrompt">
        <Stack class="items-center">
          <AiSparklesIcon size="large" />
          <Typography variant="body" muted>
            Use the power of AI to generate a full profile for this Account.
          </Typography>
          <Button
            :dt="button"
            @click="confirmDisclaimerAccepted"
          >
            <SparklesIcon />
            Generate Profile
          </Button>
        </Stack>
      </Jumbotron>
      <Toolbar v-else align="left">
        <Button
          v-if="!hasGeneratedProfile"
          severity="secondary"
          :dt="button"
          @click="confirmDisclaimerAccepted"
        >
          <SparklesIcon />
          Generate Profile
        </Button>
      </Toolbar>
    </FeatureGate>
    <template v-if="sections.length > 0">
      <AccountProfileSection
        v-for="section in sections"
        :key="section.key"
        :section="section"
        :is-loading="section.isLoading"
        :account-id="props.accountId"
        :allowed-attempts="allowedSectionAttempts"
        :has-generated-profile="hasGeneratedProfile"
        :accepted-disclaimer="isDisclaimerAccepted"
        :is-section-loading="isSectionLoadingOnPageOpen || isSectionLoading"
        @edit="onSectionEdit"
        @on:loading="onSectionLoading"
      />
    </template>
    <Typography v-else variant="body" muted>
      No profile available for this Account
    </Typography>
  </Stack>
  <ConfirmDialog :group="CONFIRM_DIALOG_GROUP" />
  <ConfirmDialog group="ProfileDisclaimer">
    <template #message>
      <AiDisclaimer />
    </template>
  </ConfirmDialog>
  <ConfirmDialog group="aiCreateDuplicateConfirm" />
</template>
